import { DailyNavRecord } from 'api/models/DailyNavRecord';

export const DAILY_NAV_SPREADSHEET_NAME = 'daily-nav-spreadsheet';
export const DAILY_NAV_SPREADSHEET_TITLE = 'Company';
export const DAILY_NAV_SPREADSHEET_TITLE_ALIAS = 'title';
export const DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS = 'sections';

export const DAILY_NAV_COLUMN_TYPES = {
  VALUATION_DATE: 'valuation_date',
  EQUITY_VALUE: 'equity_value',
  PRICE_PER_SHARE: 'price_per_share',
  SHORT_WINDOW_DELTA: 'short_window_delta',
  LONG_WINDOW_DELTA: 'long_window_delta',
  SINCE_LAST_VALUATION_DELTA: 'since_last_valuation_delta',
  STATUS: 'status',
} as const;

export const DAILY_NAV_COLUMN_ORDER = [
  DAILY_NAV_COLUMN_TYPES.VALUATION_DATE,
  DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE,
  DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE,
  DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA,
  DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA,
  DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA,
  DAILY_NAV_COLUMN_TYPES.STATUS,
];

// Session counts ride on each record's breakdown; the grid never fetches the
// firm template. Omit them for plain "Short"/"Long" wording.
export const getDailyNavColumnLabels = (
  shortWindowSessions?: number | null,
  longWindowSessions?: number | null
): Record<string, string> => ({
  [DAILY_NAV_COLUMN_TYPES.VALUATION_DATE]: 'Valuation Date',
  [DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE]: 'Equity Value',
  [DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE]: 'Price / Share',
  [DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA]: shortWindowSessions ? `${shortWindowSessions}-Session Δ` : 'Short Δ',
  [DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA]: longWindowSessions ? `${longWindowSessions}-Session Δ` : 'Long Δ',
  [DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA]: 'Last Val Δ',
  [DAILY_NAV_COLUMN_TYPES.STATUS]: 'Status',
});

// Static fallback for callers with no row data on hand.
export const DAILY_NAV_COLUMN_LABELS: Record<string, string> = getDailyNavColumnLabels();

// Column-group bands rendered above the column labels, mirroring the monitoring
// prototype's PREVIOUS VALUATION / NAV CONSIDERATIONS sections.
export interface DailyNavSection {
  label: string;
  columnTypes: string[];
  // Completed at render time with the metric's display name from the breakdown
  // payload, so the header follows whatever metric produced the deltas.
  labelFromMetric?: boolean;
}

export const DAILY_NAV_SECTIONS: DailyNavSection[] = [
  {
    label: 'Previous Valuation',
    columnTypes: [
      DAILY_NAV_COLUMN_TYPES.VALUATION_DATE,
      DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE,
      DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE,
    ],
  },
  // Averaged metric change across the company config's comps, from Alexandria:
  // short window, long window, and since the last valuation.
  {
    label: 'Public Comp',
    labelFromMetric: true,
    columnTypes: [
      DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA,
      DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA,
      DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA,
    ],
  },
  {
    label: 'NAV Considerations',
    columnTypes: [DAILY_NAV_COLUMN_TYPES.STATUS],
  },
];

// The Review Price warning is active when a CapIQ delta window has breached its
// effective alert threshold — computed server-side into cap_iq_delta_exceeded —
// and the price has not been marked adjusted. Marking the price adjusted in the
// Review Price dialog clears the warning; otherwise it stays lit. The Status
// column's button styling keys off this.
export const dailyNavRecordNeedsReview = (record: DailyNavRecord): boolean =>
  Boolean(record.cap_iq_delta_exceeded) && !record.price_adjusted;

export const REVIEW_PRICE_LABEL = 'Review Price';

// Report-date query param. Deliberately distinct from the shared 'date' param, which
// Intelligence's measurement-date filter owns; NAV dates are free-standing daily values.
export const DAILY_NAV_REPORT_DATE_PARAM = 'nav_date';
